import * as qs from "query-string";
import React, { useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as _ from "lodash-es";
import { useForm, Controller } from "react-hook-form";
import SupplierService from "../../../../services/SupplierService";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import UiContext from "../../../../contexts/UiContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import SortOrderEnum from "../../../../models/SortOrderEnum";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import ICategory from "@models/ICategory";

type Props = {
    searchFn: (queryString: string, searchResults: any, reset: boolean) => void;
    sortDir: SortOrderEnum;
    sortCol: string;
    categories: ICategory[];
};

interface FormValues {
    name: string;
    state_license: string;
    filter_inactive: boolean;
    category: string;
}

// Custom field component for supplier-specific inputs
const SupplierField = ({ name, label, onEnter, control, errors, ...props }: any) => {
    const supplierModel = useContext(SupplierContext);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && onEnter) {
            e.preventDefault();
            onEnter();
        }
    };

    return (
        <div className="">
            <OwLabel title={label} />
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <OwFnInput
                        className={"form-control-sm text-dark " + (props.className || "")}
                        name={name}
                        value={field.value}
                        fn={field.onChange}
                        placeholder={props.placeholder}
                        supplierModel={supplierModel}
                        isSupplierVendorOverride={true}
                        onKeyDownFn={handleKeyDown}
                        enableDebounce={true}
                        debounceMs={300}
                    />
                )}
            />
            {errors[name] && <div className="text-danger text-xs mt-1">{errors[name].message}</div>}
        </div>
    );
};

export const VendorsFilter = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;

    const navigate = useNavigate();
    const location = useLocation();

    const yesNoSelectOptions = [
        { title: "yes", value: "Y" },
        { title: "no", value: "N" },
    ];

    const parseUrlForFilter = (): string | undefined => {
        const parsedSearch = qs.parse(location.search);
        const name = _.get(parsedSearch, "name", undefined);

        if (name && _.isString(name)) {
            return name;
        } else {
            return undefined;
        }
    };

    // Get initial values from URL or defaults
    const getInitialValues = (): FormValues => {
        const parsedSearch = qs.parse(location.search);
        return {
            name: parseUrlForFilter() || "",
            state_license: _.get(parsedSearch, "state_license", "") as string,
            filter_inactive: _.get(parsedSearch, "filter_inactive", "true") === "true",
            category: _.get(parsedSearch, "category", "") as string,
        };
    };

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: getInitialValues(),
    });

    const watchedValues = watch();

    useEffect(() => {
        // Trigger initial search when component mounts
        handleSearch(getInitialValues());
    }, []);

    // Handle form submission
    const handleSearch = (values: FormValues) => {
        const query: string[] = [];
        let queryString = "";

        if (values.name) {
            query.push(`name=${encodeURIComponent(values.name)}`);
        }

        query.push(`filter_inactive=${values.filter_inactive}`);

        if (values.state_license) {
            query.push(`state_license=${encodeURIComponent(values.state_license)}`);
        }

        if (!_.isNil(props.sortDir)) {
            query.push(`order_dir=${props.sortDir}`);
        }

        if (values.category) {
            query.push(`category=${encodeURIComponent(values.category)}`);
        }

        if (query.length) {
            queryString = "?";
            queryString += query.join("&");
        }

        let url = `/vendors`;
        url = SupplierService.updateStringWithVendorOverride(url, supplierModel);

        navigate(`${url}${queryString}`);
        props.searchFn(queryString, [], false);
    };

    // Handle form reset
    const handleReset = () => {
        const resetValues = {
            name: "",
            state_license: "",
            filter_inactive: true,
            category: "",
        };

        reset(resetValues);

        let url = `/vendors`;
        url = SupplierService.updateStringWithVendorOverride(url, supplierModel);

        navigate(`${url}`);
        props.searchFn("?filter_inactive=true", [], true);
    };

    const onSubmit = (data: FormValues) => {
        handleSearch(data);
    };

    return (
        <div className={"VendorsFilter mb-2"}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={"row d-flex align-items-end"}>
                    <div className={"col-12 col-sm-8 col-md-3"}>
                        <div className="position-relative">
                            <SupplierField
                                name="name"
                                label="name"
                                className="text-dark"
                                placeholder="vendor_name"
                                control={control}
                                errors={errors}
                            />
                        </div>
                    </div>

                    {props.categories.length > 0 && (
                        <div className="col-auto">
                            <OwLabel title={"category"} />
                            <Controller
                                name="category"
                                control={control}
                                render={({ field }) => (
                                    <OwSelectOption
                                        options={props.categories}
                                        translateTitle={false}
                                        titleProp={"name"}
                                        valueProp={"id"}
                                        name="category"
                                        fn={(e: any) => {
                                            // Handle either direct event or custom event
                                            const value = e.target ? e.target.value : e;
                                            field.onChange(value);
                                        }}
                                        selectedOption={field.value}
                                        hasSelectedOptionProp={false}
                                    />
                                )}
                            />
                        </div>
                    )}

                    {supplierModel.has_account_state_license ? (
                        <div className={"col-auto"}>
                            <OwLabel title={"state_license"} />
                            <Controller
                                name="state_license"
                                control={control}
                                render={({ field }) => (
                                    <OwSelectOption
                                        showBlankFirst={true}
                                        options={yesNoSelectOptions}
                                        translateTitle={true}
                                        fn={(e: any) => {
                                            // Handle either direct event or custom event
                                            const value = e.target ? e.target.value : e;
                                            field.onChange(value);
                                        }}
                                        titleProp={"title"}
                                        valueProp={"value"}
                                        name="state_license"
                                        selectedOption={field.value}
                                        hasSelectedOptionProp={false}
                                    />
                                )}
                            />
                            {errors.state_license && <div className="text-danger">{errors.state_license.message}</div>}
                        </div>
                    ) : null}

                    <div className={"col-auto"}>
                        <Controller
                            name="filter_inactive"
                            control={control}
                            render={({ field }) => (
                                <OwCheckbox
                                    name="filter_inactive"
                                    fn={() => {
                                        // Simply toggle the current value
                                        field.onChange(!field.value);
                                    }}
                                    checked={field.value}
                                    truthyVal={true}
                                    falsyVal={false}
                                    label={"filter_inactive"}
                                />
                            )}
                        />
                    </div>

                    <div className={"col-auto"}>
                        <OwLabel title={" "} skipTranslate={true} />
                        <div className="ms-sm-2 mt-2 mt-sm-0">
                            <OwButton title={"search"} fn={handleSubmit(onSubmit)} />
                            <OwButton title={"reset"} fn={handleReset} className={"btn-danger ms-2"} />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default VendorsFilter;
